import React, { useState } from 'react';
import { UserPlus, Search, Shield, MoreHorizontal, Activity, Users as UsersIcon } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

const seedUsers = [
  { name: 'Iowolab Lawal', email: 'iowolab@gmail.com', role: 'superadmin', status: 'Active', lastSeen: 'Just now' },
  { name: 'Sarah Mitchell', email: 's.mitchell@teckube.io', role: 'admin', status: 'Active', lastSeen: '2h ago' },
  { name: 'Daniel Okafor', email: 'd.okafor@infosys.com', role: 'analyst', status: 'Active', lastSeen: '6h ago' },
  { name: 'Marta Rivera', email: 'm.rivera@assurance.eu', role: 'auditor', status: 'Active', lastSeen: '1d ago' },
  { name: 'James Park', email: 'j.park@compliance.co.uk', role: 'viewer', status: 'Pending', lastSeen: 'Invited 2d ago' },
];

const auditLog = [
  { actor: 'iowolab@gmail.com', action: 'ran compliance check', target: 'SEC_CLIMATE · Infosys', when: '5/9/2026 1:13 AM' },
  { actor: 'iowolab@gmail.com', action: 'exported CSV', target: 'Check History', when: '5/9/2026 1:11 AM' },
  { actor: 's.mitchell@teckube.io', action: 'signed in', target: 'Web session', when: '5/8/2026 6:42 PM' },
  { actor: 'd.okafor@infosys.com', action: 'downloaded PDF', target: 'CSRD report #4', when: '5/8/2026 3:21 PM' },
  { actor: 'iowolab@gmail.com', action: 'updated rule pack', target: 'ISSB v1.0', when: '5/7/2026 11:08 AM' },
];

const roleTone = {
  superadmin: 'bg-[#0B1F2D] text-[#FAFAF5]',
  admin: 'bg-[#E8F3EE] text-[#1B6F5E]',
  analyst: 'bg-[#EEEEF7] text-[#3D3D8A]',
  auditor: 'bg-[#FBF1E5] text-[#A66A23]',
  viewer: 'bg-[#FAFAF5] text-[#0B1F2D]/65 border border-[#E8E5DC]',
};

export default function Admin() {
  const [search, setSearch] = useState('');
  const rows = seedUsers.filter(
    (u) => u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-[1280px]">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11.5px] tracking-[0.16em] uppercase text-[#1B6F5E] font-medium">
            Workspace administration
          </div>
          <h2 className="mt-2 font-display text-[36px] lg:text-[44px] leading-[1.05] text-[#0B1F2D]">
            Admin.
          </h2>
          <p className="mt-3 text-[14.5px] leading-[1.55] text-[#0B1F2D]/65 max-w-[640px]">
            Manage workspace members, roles, and audit log. Only superadmins can change roles.
          </p>
        </div>
        <Button className="bg-[#0B1F2D] hover:bg-[#0B1F2D]/90 text-[#FAFAF5] rounded-full h-11 px-5 text-[13px] inline-flex items-center gap-2">
          <UserPlus className="w-3.5 h-3.5" />
          Invite member
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        {[
          { label: 'Workspace members', value: '5', Ico: UsersIcon },
          { label: 'Active this week', value: '4', Ico: Activity },
          { label: 'Open invitations', value: '1', Ico: Shield },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-[#E8E5DC] bg-white p-6 flex items-start justify-between">
            <div>
              <div className="text-[12px] uppercase tracking-[0.12em] text-[#0B1F2D]/55">{s.label}</div>
              <div className="mt-3 font-display text-[36px] leading-none text-[#0B1F2D]">{s.value}</div>
            </div>
            <div className="w-10 h-10 rounded-lg bg-[#E8F3EE] text-[#1B6F5E] flex items-center justify-center">
              <s.Ico className="w-4.5 h-4.5" />
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Members table */}
        <div className="lg:col-span-8 rounded-2xl border border-[#E8E5DC] bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-[#E8E5DC] flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="font-display text-[18px] text-[#0B1F2D] flex-1">Members</div>
            <div className="relative w-full sm:w-[260px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#0B1F2D]/45" />
              <Input
                placeholder="Search members..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-10 pl-9 bg-[#FAFAF5] border-[#E8E5DC] focus-visible:ring-[#1B6F5E]"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#FAFAF5]/60 border-b border-[#E8E5DC]">
                <tr className="text-[10.5px] tracking-[0.14em] uppercase text-[#0B1F2D]/55">
                  <th className="px-5 py-3 font-medium">Member</th>
                  <th className="px-5 py-3 font-medium">Role</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Last seen</th>
                  <th className="px-5 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E5DC]">
                {rows.map((u) => (
                  <tr key={u.email} className="hover:bg-[#FAFAF5]/60 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#E8F3EE] text-[#1B6F5E] flex items-center justify-center text-[11px] font-semibold">
                          {u.name.split(' ').map((n) => n[0]).slice(0, 2).join('')}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[13px] text-[#0B1F2D] font-medium truncate">{u.name}</div>
                          <div className="text-[11.5px] text-[#0B1F2D]/55 truncate">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-[10.5px] uppercase tracking-[0.1em] font-medium px-2 py-1 rounded-md ${roleTone[u.role]}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 text-[11.5px] ${
                          u.status === 'Active' ? 'text-[#1B6F5E]' : 'text-[#A66A23]'
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            u.status === 'Active' ? 'bg-[#1B6F5E]' : 'bg-[#A66A23]'
                          }`}
                        />
                        {u.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-[12px] text-[#0B1F2D]/65">{u.lastSeen}</td>
                    <td className="px-5 py-4 text-right">
                      <button className="w-8 h-8 rounded-md hover:bg-[#FAFAF5] flex items-center justify-center text-[#0B1F2D]/55">
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Audit log */}
        <div className="lg:col-span-4 rounded-2xl border border-[#E8E5DC] bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-[18px] text-[#0B1F2D]">Audit log</div>
            <span className="font-mono-tight text-[11px] text-[#0B1F2D]/55">/recent</span>
          </div>
          <ul className="space-y-3.5">
            {auditLog.map((a, i) => (
              <li key={i} className="flex gap-3">
                <div className="shrink-0 w-2 h-2 mt-2 rounded-full bg-[#1B6F5E]" />
                <div className="min-w-0">
                  <div className="text-[12.5px] text-[#0B1F2D] leading-[1.5]">
                    <span className="font-medium">{a.actor}</span>{' '}
                    <span className="text-[#0B1F2D]/65">{a.action}</span>
                  </div>
                  <div className="text-[11.5px] text-[#0B1F2D]/55 mt-0.5 font-mono-tight">{a.target}</div>
                  <div className="text-[10.5px] text-[#0B1F2D]/45 mt-1">{a.when}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
